import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { PointsBadge } from '@/components/PointsBadge'
import {
  type FootballMatch,
  type LineupPlayer,
  type MatchEvent,
  type MatchPredictionStatsResponse,
  useMatchChatAccess,
  useMatchDetail,
  useOtherPredictions,
  usePredictionStats,
} from '@/features/poules/api'
import { formatKickoffLongDateTime, getKickoffSource } from '@/lib/kickoff'
import { getLocale } from '@/config/locale'
import { translate, useTranslation } from '@/i18n'

export function MatchDetailPage() {
  const { t } = useTranslation()
  const { matchId, pouleId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isLineupsOpen, setIsLineupsOpen] = useState(false)
  const [isPredictionsOpen, setIsPredictionsOpen] = useState(false)
  const [isStatsOpen, setIsStatsOpen] = useState(false)
  const numericMatchId = parsePositiveInt(matchId)
  const numericPouleId = parsePositiveInt(pouleId)
  const numericPouleMatchId = parsePositiveInt(searchParams.get('pouleMatchId'))
  const sourceTab = normalizeSourceTab(searchParams.get('sourceTab'))
  const detailQuery = useMatchDetail(numericMatchId, {
    pouleMatchId: numericPouleMatchId,
    useGenericFallback: numericPouleId == null,
  })
  const match = detailQuery.data
  const resolvedPouleId = numericPouleId ?? match?.poule?.id ?? null
  const resolvedPouleMatchId = numericPouleMatchId ?? match?.poule_match_id ?? null
  const hasPouleContext = resolvedPouleId != null && resolvedPouleMatchId != null
  const otherPredictionsQuery = useOtherPredictions(
    resolvedPouleId,
    resolvedPouleMatchId,
    isPredictionsOpen,
  )
  const predictionStatsQuery = usePredictionStats(
    resolvedPouleId,
    resolvedPouleMatchId,
    isStatsOpen,
  )
  const chatAccess = useMatchChatAccess()

  const canShowPredictionsAndStats =
    hasPouleContext &&
    match != null &&
    (match.status === 'live' || match.status === 'ht' || match.status === 'ft')
  const canShowChat = hasPouleContext && match?.chat_active === true
  const canShowLineups = hasLineups(match)
  const isOverlayOpen = isLineupsOpen || isPredictionsOpen || isStatsOpen

  useEffect(() => {
    if (!isOverlayOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOverlayOpen])

  if (numericMatchId == null) {
    return (
      <PageState
        title={t('Ongeldige wedstrijd')}
        body={t('De URL bevat geen geldig wedstrijd-id.')}
        tone="error"
      />
    )
  }

  if (detailQuery.isLoading) {
    return (
      <PageState
        title={t('Wedstrijddetail laden')}
        body={t('We halen de laatste details, events en opstellingen op.')}
      />
    )
  }

  if (detailQuery.isError || !match) {
    return (
      <PageState
        title={t('Wedstrijddetail niet geladen')}
        body={t('De detailpagina kon niet worden opgebouwd met de huidige backenddata.')}
        tone="error"
        cta={t('Opnieuw proberen')}
        onClick={() => void detailQuery.refetch()}
      />
    )
  }

  return (
    <>
      <div className="space-y-5 pb-28">
        <section className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              if (numericPouleId != null) {
                const nextParams = new URLSearchParams()
                if (sourceTab !== 'dashboard') {
                  nextParams.set('tab', sourceTab)
                }
                navigate(
                  `/poules/${numericPouleId}${nextParams.size > 0 ? `?${nextParams.toString()}` : ''}`,
                )
                return
              }

              navigate(-1)
            }}
            className="inline-flex items-center gap-2 rounded-full border border-[#202427] bg-[#101415] px-3.5 py-2 text-sm font-semibold text-surface-200 transition hover:border-[#24405b] hover:bg-[#151a1c]"
          >
            <span aria-hidden="true">‹</span>
            <span>{t('Terug')}</span>
          </button>

          <div className="flex flex-wrap justify-end gap-2">
            {match.poule?.name ? <MetaPill>{match.poule.name}</MetaPill> : null}
            {match.stage_name ? <MetaPill>{match.stage_name}</MetaPill> : null}
            {match.joker ? (
              <MetaPill accent>
                {typeof match.joker_multiplier === 'number' && match.joker_multiplier > 1
                  ? `Joker x${match.joker_multiplier}`
                  : 'Joker'}
              </MetaPill>
            ) : null}
          </div>
        </section>

        <MatchHeaderCard match={match} />
        <MatchEventsSection match={match} />
      </div>

      <StickyActionBar
        items={[
          canShowLineups
            ? {
                label: t('Opstellingen'),
                onClick: () => setIsLineupsOpen(true),
              }
            : null,
          canShowPredictionsAndStats
            ? {
                label: t('Voorspellingen'),
                onClick: () => setIsPredictionsOpen(true),
              }
            : null,
          canShowPredictionsAndStats
            ? {
                label: t('Stats'),
                onClick: () => setIsStatsOpen(true),
              }
            : null,
          canShowChat
            ? {
                label: chatAccess.isPending ? t('Chat openen…') : t('Chat'),
                onClick: () => {
                  if (resolvedPouleMatchId == null) return
                  chatAccess.mutate(
                    { matchId: match.id, pouleMatchId: resolvedPouleMatchId },
                    {
                      onSuccess: () => {
                        navigate(`/wedstrijden/${match.id}/chat`, {
                          state: {
                            homeName: match.home_team.name,
                            awayName: match.away_team.name,
                            homeLogo: match.home_team.logo_url,
                            awayLogo: match.away_team.logo_url,
                            kickoff: getKickoffSource(match.kickoff_utc, match.kickoff_nl),
                            writable: match.chat_writable === true,
                          },
                        })
                      },
                    },
                  )
                },
                disabled: chatAccess.isPending,
              }
            : null,
        ]}
        errorMessage={chatAccess.error instanceof Error ? chatAccess.error.message : undefined}
      />

      {isLineupsOpen ? (
        <LineupsSheet match={match} onClose={() => setIsLineupsOpen(false)} />
      ) : null}

      {isPredictionsOpen ? (
        <OtherPredictionsSheet
          onClose={() => setIsPredictionsOpen(false)}
          match={match}
          query={otherPredictionsQuery}
        />
      ) : null}

      {isStatsOpen ? (
        <PredictionStatsSheet
          onClose={() => setIsStatsOpen(false)}
          match={match}
          query={predictionStatsQuery}
        />
      ) : null}
    </>
  )
}

function MatchHeaderCard({ match }: { match: FootballMatch }) {
  const { t } = useTranslation()
  const predictionPoints = getPredictionPoints(match)
  const status = getMatchStatusDisplay(match)

  return (
    <section className="overflow-hidden rounded-[1.45rem] border border-[#202427] bg-[radial-gradient(circle_at_top,_rgba(18,49,74,0.24),_transparent_42%),linear-gradient(180deg,_rgba(9,14,21,0.98),_rgba(4,7,12,1))] p-5 shadow-[0_18px_56px_rgba(0,0,0,0.34)] sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-surface-500">
            {t('Wedstrijddetail')}
          </p>
          <h1 className="mt-2 text-2xl font-black tracking-tight text-white sm:text-[2rem]">
            {match.home_team.name} vs {match.away_team.name}
          </h1>
        </div>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_auto_1fr] lg:items-center">
        <TeamSummary team={match.home_team} form={match.home_form} align="left" />

        <div className="flex min-w-[14rem] flex-col items-center justify-center text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-surface-500">
            {formatKickoffLabel(match.kickoff_utc, match.kickoff_nl, match.kickoff_time_tbd)}
          </p>
          <div className="mt-3 flex items-center justify-center gap-3">
            <span className="text-4xl font-black tracking-[-0.06em] text-white sm:text-5xl">
              {formatScoreValue(match.score?.home)}
            </span>
            <span className="text-2xl font-black text-surface-600">-</span>
            <span className="text-4xl font-black tracking-[-0.06em] text-white sm:text-5xl">
              {formatScoreValue(match.score?.away)}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs text-surface-400">
            {match.stage_name ? <span>{match.stage_name}</span> : null}
            {match.stage_name && getSubMeta(match) ? (
              <span aria-hidden="true" className="h-1 w-1 rounded-full bg-surface-700" />
            ) : null}
            {getSubMeta(match) ? <span>{getSubMeta(match)}</span> : null}
          </div>
        </div>

        <TeamSummary team={match.away_team} form={match.away_form} align="right" />
      </div>

      {match.user_prediction &&
      typeof match.user_prediction.home_pred === 'number' &&
      typeof match.user_prediction.away_pred === 'number' ? (
        <div className="mt-6 rounded-[1.05rem] border border-[#182332] bg-[#09111a] px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                {t('Jouw voorspelling')}
              </p>
              <p className="mt-2 text-lg font-black text-white">
                {match.user_prediction.home_pred}-{match.user_prediction.away_pred}
              </p>
            </div>
            {typeof predictionPoints === 'number' ? (
              <div className="flex items-center gap-2">
                <PointsBadge points={predictionPoints} />
                {match.status === 'live' || match.status === 'ht' ? (
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-surface-500">
                    {t('Virtueel')}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {!match.is_predictable && match.prediction_locked_by_stage_release ? (
        <div className="mt-4 rounded-[1rem] border border-amber-900/40 bg-amber-950/20 px-4 py-3 text-sm text-amber-100">
          {match.prediction_locked_reason ||
            getStageLockCopy(match.prediction_lock_context?.current_stage_name)}
        </div>
      ) : null}
    </section>
  )
}

function MatchEventsSection({ match }: { match: FootballMatch }) {
  const { t } = useTranslation()
  const mainEvents = useMemo(() => buildMainEvents(match.events ?? []), [match.events])
  const substitutions = useMemo(
    () => (match.events ?? []).filter((event) => normalizeEventType(event.type) === 'sub'),
    [match.events],
  )

  return (
    <section className="rounded-[1.35rem] border border-[#202427] bg-[#0a0d0f] p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
            {t('Events')}
          </p>
          <h2 className="mt-2 text-xl font-black tracking-tight text-white">{t('Wedstrijdverloop')}</h2>
        </div>
        <span className="rounded-full border border-[#202427] bg-[#0d1113] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-surface-400">
          {match.events?.length ?? 0} items
        </span>
      </div>

      {mainEvents.length === 0 ? (
        <EmptyPanel
          className="mt-4"
          title={t('Nog geen goals of kaarten')}
          body={t('Zodra de hoofd-events beschikbaar zijn, verschijnen ze hier.')}
        />
      ) : (
        <div className="mt-4 space-y-3">
          {mainEvents.map((item) => (
            <EventRow key={item.event.id} event={item.event} additions={item.additions} />
          ))}
        </div>
      )}

      <div className="mt-6 border-t border-[#142030] pt-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-black uppercase tracking-[0.16em] text-white">{t('Wissels')}</h3>
          <span className="text-xs text-surface-500">{substitutions.length}</span>
        </div>

        {substitutions.length === 0 ? (
          <EmptyPanel
            className="mt-4"
            title={t('Nog geen wissels')}
            body={t('Wisselmomenten verschijnen hier zodra ze beschikbaar zijn.')}
          />
        ) : (
          <div className="mt-4 space-y-3">
            {substitutions.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

function LineupsSheet({
  match,
  onClose,
}: {
  match: FootballMatch
  onClose: () => void
}) {
  const { t } = useTranslation()

  return (
    <OverlaySheet
      title={t('Opstellingen')}
      subtitle={t('Startende elf en bank per team')}
      onClose={onClose}
      size="wide"
    >
      {!hasLineups(match) ? (
        <EmptyPanel
          title={t('Geen opstellingen beschikbaar')}
          body={t('Deze wedstrijd bevat nog geen lineup- of bankdata.')}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <LineupColumn
            team={match.home_team}
            lineup={match.homeLineup ?? []}
            bench={match.homeBench ?? []}
          />
          <LineupColumn
            team={match.away_team}
            lineup={match.awayLineup ?? []}
            bench={match.awayBench ?? []}
          />
        </div>
      )}
    </OverlaySheet>
  )
}

function OtherPredictionsSheet({
  match,
  query,
  onClose,
}: {
  match: FootballMatch
  query: ReturnType<typeof useOtherPredictions>
  onClose: () => void
}) {
  const { t } = useTranslation()

  return (
    <OverlaySheet
      title={t('Voorspellingen')}
      subtitle={t('Andere voorspellingen voor {home} - {away}', {
        home: match.home_team.name,
        away: match.away_team.name,
      })}
      onClose={onClose}
    >
      {query.isLoading ? (
        <InfoPanel title={t('Voorspellingen laden')} body={t('We halen de voorspellingen van pouleleden op.')} />
      ) : query.isError ? (
        <ErrorPanel
          title={t('Voorspellingen niet geladen')}
          body={t('De voorspellingen van andere pouleleden konden niet worden geladen.')}
          cta={t('Opnieuw proberen')}
          onClick={() => void query.refetch()}
        />
      ) : (query.data?.predictions.length ?? 0) === 0 ? (
        <EmptyPanel
          title={t('Nog geen voorspellingen')}
          body={t('Er zijn nog geen andere voorspellingen beschikbaar voor deze wedstrijd.')}
        />
      ) : (
        <div className="space-y-3">
          {query.data?.predictions.map((item, index) => (
            <article
              key={`${item.user_name}-${index}`}
              className="flex items-center gap-3 rounded-[1rem] border border-[#202427] bg-[#0d1011] px-3.5 py-3"
            >
              <Avatar name={item.user_name} image={item.user?.profile_image} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">{item.user_name}</p>
                <p className="mt-1 text-xs text-surface-400">
                  {t('Voorspelling')} {item.home_pred}-{item.away_pred}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-black text-white">
                  {typeof item.points_awarded === 'number'
                    ? `${item.points_awarded} pt`
                    : typeof item.provisional_points === 'number'
                      ? `${item.provisional_points} virtueel`
                      : t('Nog geen punten')}
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
    </OverlaySheet>
  )
}

function PredictionStatsSheet({
  match,
  query,
  onClose,
}: {
  match: FootballMatch
  query: ReturnType<typeof usePredictionStats>
  onClose: () => void
}) {
  const { t } = useTranslation()

  return (
    <OverlaySheet
      title={t('Voorspellingsstatistieken')}
      subtitle={t('Verdeling en favoriete uitslagen voor {home} - {away}', {
        home: match.home_team.name,
        away: match.away_team.name,
      })}
      onClose={onClose}
    >
      {query.isLoading ? (
        <InfoPanel title={t('Stats laden')} body={t('We bouwen de prediction stats voor deze wedstrijd op.')} />
      ) : query.isError ? (
        query.error instanceof ApiError && query.error.status === 403 ? (
          <EmptyPanel
            title={t('Stats nog gelockt')}
            body={t('Stats zijn beschikbaar zodra de wedstrijd is begonnen.')}
          />
        ) : (
          <ErrorPanel
            title={t('Stats niet geladen')}
            body={t('De prediction stats konden niet worden geladen.')}
            cta={t('Opnieuw proberen')}
            onClick={() => void query.refetch()}
          />
        )
      ) : !query.data ? (
        <EmptyPanel title={t('Geen stats beschikbaar')} body={t('Voor deze wedstrijd zijn nog geen stats beschikbaar.')} />
      ) : (
        <PredictionStatsContent data={query.data} />
      )}
    </OverlaySheet>
  )
}

function PredictionStatsContent({ data }: { data: MatchPredictionStatsResponse }) {
  const { t } = useTranslation()

  return (
    <div className="space-y-5">
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label={t('Geaccepteerd')}
          value={String(data.summary.accepted_members_count)}
        />
        <MetricCard
          label={t('Voorspeld')}
          value={String(data.summary.submitted_predictions_count)}
        />
        <MetricCard
          label={t('Ontbreekt')}
          value={String(data.summary.missing_predictions_count)}
        />
        <MetricCard
          label={t('Dekking')}
          value={`${Math.round(data.summary.coverage_percentage)}%`}
        />
      </section>

      <section className="rounded-[1rem] border border-[#202427] bg-[#0d1011] p-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
          {t('Meest gebruikte voorspelling')}
        </p>
        {data.most_used_prediction ? (
          <div className="mt-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-lg font-black text-white">{data.most_used_prediction.label}</p>
              <p className="mt-1 text-sm text-surface-400">
                {data.most_used_prediction.count} {t('voorspellingen')}
              </p>
            </div>
            <MetaPill accent>{Math.round(data.most_used_prediction.percentage)}%</MetaPill>
          </div>
        ) : (
          <p className="mt-3 text-sm text-surface-400">{t('Nog geen populairste uitslag beschikbaar.')}</p>
        )}
      </section>

      <section className="rounded-[1rem] border border-[#202427] bg-[#0d1011] p-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
          {t('Verdeling van uitkomsten')}
        </p>
        {!data.outcome_distribution || data.outcome_distribution.items.length === 0 ? (
          <p className="mt-3 text-sm text-surface-400">{t('Nog geen outcome-verdeling beschikbaar.')}</p>
        ) : (
          <div className="mt-4 space-y-3">
            {data.outcome_distribution.items.map((item) => (
              <div key={item.key}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-semibold text-white">
                    {getOutcomeLabel(item.key, data)}
                  </span>
                  <span className="text-surface-400">
                    {item.count} • {Math.round(item.percentage)}%
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#111a27]">
                  <div
                    className="h-full rounded-full bg-[#3f7890]"
                    style={{ width: `${Math.max(0, Math.min(100, item.percentage))}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-[1rem] border border-[#202427] bg-[#0d1011] p-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
          {t('Populairste uitslagen')}
        </p>
        {data.top_scorelines.length === 0 ? (
          <p className="mt-3 text-sm text-surface-400">{t('Nog geen top scorelines beschikbaar.')}</p>
        ) : (
          <div className="mt-4 space-y-3">
            {data.top_scorelines.map((item, index) => (
              <div
                key={`${item.label}-${index}`}
                className="flex items-center justify-between gap-3 rounded-[0.95rem] bg-[#0c131d] px-3.5 py-3"
              >
                <div>
                  <p className="text-sm font-semibold text-white">{item.label}</p>
                  <p className="mt-1 text-xs text-surface-400">{item.count} {t('voorspellingen')}</p>
                </div>
                <MetaPill accent>{Math.round(item.percentage)}%</MetaPill>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function StickyActionBar({
  items,
  errorMessage,
}: {
  items: Array<{ label: string; onClick: () => void; disabled?: boolean } | null>
  errorMessage?: string | undefined
}) {
  const visibleItems = items.filter(Boolean) as Array<{
    label: string
    onClick: () => void
    disabled?: boolean
  }>

  if (visibleItems.length === 0 && !errorMessage) return null

  return (
    <div className="sticky bottom-4 z-20 mt-4">
      <div className="rounded-[1.15rem] border border-[#202427] bg-[rgba(6,10,16,0.94)] p-3 shadow-[0_16px_48px_rgba(0,0,0,0.34)] backdrop-blur-xl">
        {visibleItems.length > 0 ? (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {visibleItems.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={item.onClick}
                disabled={item.disabled}
                className="rounded-[0.9rem] bg-[#3f7890] px-4 py-3 text-sm font-black text-white transition hover:bg-[#5b91a2] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}

        {errorMessage ? (
          <p className="mt-2 rounded-[0.9rem] bg-red-950/35 px-3 py-2 text-center text-xs text-red-200">
            {errorMessage}
          </p>
        ) : null}
      </div>
    </div>
  )
}

function OverlaySheet({
  title,
  subtitle,
  onClose,
  children,
  size = 'default',
}: {
  title: string
  subtitle: string
  onClose: () => void
  children: React.ReactNode
  size?: 'default' | 'wide'
}) {
  return (
    <div className="animate-drawer-backdrop-in fixed inset-0 z-[70] bg-black/88 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[80vh] w-full items-stretch px-3 pb-3 sm:px-4">
        <div
          className={[
            'animate-manager-player-picker-in relative mx-auto flex w-full flex-col overflow-hidden rounded-[1.4rem] border border-[#202427] bg-[#05080d] shadow-[0_18px_56px_rgba(0,0,0,0.5)]',
            size === 'wide' ? 'max-w-6xl' : 'max-w-3xl',
          ].join(' ')}
        >
          <div className="border-b border-[#142030] px-4 pb-4 pt-3 sm:px-5">
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-surface-700" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                  {title}
                </p>
                <h2 className="mt-2 text-xl font-black text-white">{subtitle}</h2>
              </div>
              <ModalCloseButton onClick={onClose} />
            </div>
          </div>
          <div className="overflow-y-auto p-4 sm:p-5">{children}</div>
        </div>
      </div>
    </div>
  )
}

function TeamSummary({
  team,
  form,
  align,
}: {
  team: FootballMatch['home_team']
  form?: string[] | undefined
  align: 'left' | 'right'
}) {
  const content = (
    <>
      <TeamLogo team={team} size="lg" />
      <div className={align === 'right' ? 'lg:text-right' : ''}>
        <p className="mt-3 text-lg font-black text-white">{team.name}</p>
        {form && form.length > 0 ? (
          <div
            className={[
              'mt-3 flex flex-wrap gap-1.5',
              align === 'right' ? 'lg:justify-end' : '',
            ].join(' ')}
          >
            {form.slice(0, 5).map((item, index) => (
              <span
                key={`${item}-${index}`}
                className={`h-2.5 w-2.5 rounded-full ${getFormDotClass(item)}`}
                title={getFormLabel(item)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </>
  )

  return (
    <div
      className={[
        'flex items-center gap-4',
        align === 'right' ? 'lg:flex-row-reverse lg:justify-end' : '',
      ].join(' ')}
    >
      {team.id > 0 ? (
        <Link
          to={`/teams/${team.id}`}
          className="group flex items-center gap-4 rounded-[1rem] transition hover:opacity-90"
        >
          {content}
        </Link>
      ) : (
        <div className="flex items-center gap-4">{content}</div>
      )}
    </div>
  )
}

function LineupColumn({
  team,
  lineup,
  bench,
}: {
  team: FootballMatch['home_team']
  lineup: LineupPlayer[]
  bench: LineupPlayer[]
}) {
  const { t } = useTranslation()
  const groupedLineup = groupPlayersByPosition(lineup)
  const groupedBench = groupPlayersByPosition(bench)

  return (
    <section className="rounded-[1.2rem] border border-[#202427] bg-[#0d1011] p-4">
      <div className="flex items-center gap-3">
        <TeamLogo team={team} />
        <div>
          <p className="text-lg font-black text-white">{team.name}</p>
          <p className="text-sm text-surface-400">{t('Opstellingen en bank')}</p>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <PlayerGroupSection title={t('Basiself')} groups={groupedLineup} />
        <PlayerGroupSection title={t('Bank')} groups={groupedBench} emptyCopy={t('Geen bankspelers beschikbaar.')} />
      </div>
    </section>
  )
}

function PlayerGroupSection({
  title,
  groups,
  emptyCopy = 'Geen spelers beschikbaar.',
}: {
  title: string
  groups: Array<{ key: string; label: string; players: LineupPlayer[] }>
  emptyCopy?: string
}) {
  return (
    <div>
      <h3 className="text-sm font-black uppercase tracking-[0.16em] text-white">{title}</h3>
      {groups.length === 0 ? (
        <p className="mt-3 text-sm text-surface-400">{emptyCopy}</p>
      ) : (
        <div className="mt-3 space-y-3">
          {groups.map((group) => (
            <div key={group.key}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                {group.label}
              </p>
              <div className="mt-2 space-y-2">
                {group.players.map((player, index) => (
                  <div
                    key={`${player.name}-${player.jersey ?? index}`}
                    className="flex items-center gap-3 rounded-[0.95rem] bg-[#0c131d] px-3 py-2.5"
                  >
                    <Avatar name={player.name} image={player.image} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-white">{player.name}</p>
                      <p className="mt-1 text-xs text-surface-400">
                        {player.position || translate('Positie onbekend')}
                      </p>
                    </div>
                    <span className="rounded-full bg-[#111a27] px-2 py-1 text-xs font-semibold text-surface-300">
                      #{typeof player.jersey === 'number' ? player.jersey : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function EventRow({
  event,
  additions,
}: {
  event: MatchEvent
  additions?: MatchEvent[]
}) {
  const side = normalizeTeamSide(event.team_side)
  const eventType = normalizeEventType(event.type)

  return (
    <article className="rounded-[1rem] border border-[#202427] bg-[#0d1011] px-3.5 py-3">
      <div className="flex items-start gap-3">
        <span className="min-w-[3.5rem] rounded-full bg-[#111a27] px-2.5 py-1 text-center text-xs font-black text-white">
          {formatMinute(event)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <EventTypePill type={eventType} />
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                side === 'home'
                  ? 'bg-[#10191c] text-[#93d9ff]'
                  : side === 'away'
                    ? 'bg-[#17141f] text-[#d0b8ff]'
                    : 'bg-[#111a27] text-surface-400'
              }`}
            >
              {getSideLabel(side)}
            </span>
          </div>
          <p className="mt-2 text-sm font-semibold text-white">
            {event.player || event.info || translate('Event')}
          </p>
          {event.info && event.player && event.info !== event.player ? (
            <p className="mt-1 text-xs text-surface-400">{event.info}</p>
          ) : null}
          {additions?.length ? (
            <div className="mt-3 space-y-2">
              {additions.map((item) => (
                <div
                  key={item.id}
                  className="rounded-[0.9rem] border border-amber-900/30 bg-amber-950/20 px-3 py-2 text-xs text-amber-100"
                >
                  {item.addition || item.info || translate('Goal afgekeurd')}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  )
}

function EventTypePill({ type }: { type: string }) {
  const className =
    type === 'goal'
      ? 'bg-emerald-950/35 text-emerald-200'
      : type === 'yellow'
        ? 'bg-amber-950/35 text-amber-200'
        : type === 'red'
          ? 'bg-red-950/35 text-red-200'
          : type === 'sub'
            ? 'bg-sky-950/35 text-sky-200'
            : 'bg-[#111a27] text-surface-300'

  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${className}`}>
      {getEventLabel(type)}
    </span>
  )
}

function PageState({
  title,
  body,
  tone = 'neutral',
  cta,
  onClick,
}: {
  title: string
  body: string
  tone?: 'neutral' | 'error'
  cta?: string
  onClick?: () => void
}) {
  return (
    <section
      className={[
        'rounded-[1.35rem] border p-5',
        tone === 'error'
          ? 'border-red-900/60 bg-red-950/25 text-red-100'
          : 'border-[#202427] bg-[#0a0d0f] text-surface-100',
      ].join(' ')}
    >
      <h1 className="text-xl font-black tracking-tight text-inherit">{title}</h1>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-inherit/80">{body}</p>
      {cta && onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="mt-4 rounded-full border border-current/20 px-4 py-2 text-sm font-semibold text-inherit transition hover:bg-white/5"
        >
          {cta}
        </button>
      ) : null}
    </section>
  )
}

function ErrorPanel({
  title,
  body,
  cta,
  onClick,
}: {
  title: string
  body: string
  cta: string
  onClick: () => void
}) {
  return (
    <div className="rounded-[1rem] border border-red-900/60 bg-red-950/25 p-4 text-red-100">
      <p className="text-lg font-black text-white">{title}</p>
      <p className="mt-2 text-sm text-red-100/80">{body}</p>
      <button
        type="button"
        onClick={onClick}
        className="mt-4 rounded-full border border-red-200/20 px-4 py-2 text-sm font-semibold transition hover:bg-white/5"
      >
        {cta}
      </button>
    </div>
  )
}

function InfoPanel({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-[1rem] border border-[#202427] bg-[#0d1011] p-4">
      <p className="text-lg font-black text-white">{title}</p>
      <p className="mt-2 text-sm text-surface-400">{body}</p>
    </div>
  )
}

function EmptyPanel({
  title,
  body,
  className,
}: {
  title: string
  body: string
  className?: string | undefined
}) {
  return (
    <div className={`rounded-[1rem] border border-dashed border-[#30383c] bg-[#0d1011] p-4 ${className ?? ''}`}>
      <p className="text-base font-black text-white">{title}</p>
      <p className="mt-2 text-sm text-surface-400">{body}</p>
    </div>
  )
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[1rem] border border-[#202427] bg-[#0d1011] p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-surface-500">
        {label}
      </p>
      <p className="mt-2 text-2xl font-black text-white">{value}</p>
    </div>
  )
}

function MetaPill({
  children,
  accent = false,
}: {
  children: React.ReactNode
  accent?: boolean
}) {
  return (
    <span
      className={[
        'inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]',
        accent
          ? 'border-[#203841] bg-[#10191c] text-[#93d9ff]'
          : 'border-[#202427] bg-[#0d1113] text-surface-400',
      ].join(' ')}
    >
      {children}
    </span>
  )
}

function StatusPill({
  tone,
  children,
}: {
  tone: 'live' | 'warning' | 'neutral' | 'muted'
  children: React.ReactNode
}) {
  const className =
    tone === 'live'
      ? 'border-red-500/30 bg-red-500/10 text-red-200'
      : tone === 'warning'
        ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
        : tone === 'neutral'
          ? 'border-[#203841] bg-[#10191c] text-[#93d9ff]'
          : 'border-[#202427] bg-[#0d1113] text-surface-400'

  return (
    <span className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] ${className}`}>
      {children}
    </span>
  )
}

function TeamLogo({
  team,
  size = 'md',
}: {
  team: FootballMatch['home_team']
  size?: 'md' | 'lg'
}) {
  const dimensions = size === 'lg' ? 'h-16 w-16 rounded-2xl' : 'h-10 w-10 rounded-md'

  if (team.logo_url) {
    return <img src={team.logo_url} alt="" className={`${dimensions} shrink-0 object-cover`} />
  }

  return (
    <span
      className={`flex ${dimensions} shrink-0 items-center justify-center bg-[#111a27] text-sm font-black uppercase text-surface-300`}
    >
      {getInitials(team.name)}
    </span>
  )
}

function Avatar({
  name,
  image,
  size = 'md',
}: {
  name: string
  image?: string | null | undefined
  size?: 'sm' | 'md'
}) {
  const dimensions = size === 'sm' ? 'h-9 w-9 text-[11px]' : 'h-11 w-11 text-xs'

  if (image) {
    return <img src={image} alt="" className={`${dimensions} shrink-0 rounded-full object-cover`} />
  }

  return (
    <span
      className={`flex ${dimensions} shrink-0 items-center justify-center rounded-full bg-[#111a27] font-black uppercase text-surface-300`}
    >
      {getInitials(name)}
    </span>
  )
}

function parsePositiveInt(value?: string | null) {
  if (!value) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function normalizeSourceTab(value?: string | null) {
  const normalized = value?.trim().toLowerCase()
  if (normalized === 'groups') return 'groups'
  if (normalized === 'matches') return 'matches'
  if (normalized === 'results') return 'results'
  if (normalized === 'ranking') return 'ranking'
  return 'dashboard'
}

function formatKickoffLabel(kickoffUtc?: string | null, kickoffFallback?: string | null, tbd?: boolean) {
  if (tbd) return translate('Tijd volgt nog')
  return formatKickoffLongDateTime(kickoffUtc, kickoffFallback)
}

function formatScoreValue(value?: number | null) {
  return typeof value === 'number' ? String(value) : '–'
}

function getSubMeta(match: FootballMatch) {
  if (match.group_id != null) return translate('Groep {number}', getLocale(), { number: match.group_id })
  if (match.external_group_id != null) return translate('Groep {number}', getLocale(), { number: match.external_group_id })
  return ''
}

function getPredictionPoints(match: FootballMatch) {
  if (match.status === 'live' || match.status === 'ht') {
    return match.user_prediction?.virtual_points
  }
  if (match.status === 'ft') return match.user_prediction?.points_awarded
  return undefined
}

function getMatchStatusDisplay(match: FootballMatch) {
  if (match.status === 'live') {
    return { label: translate('LIVE {minute}', getLocale(), { minute: formatMinute(match) }), tone: 'live' as const }
  }
  if (match.status === 'ht') return { label: translate('HT'), tone: 'warning' as const }
  if (match.status === 'ft') return { label: translate('FT'), tone: 'neutral' as const }
  if (match.status === 'postponed') return { label: translate('Uitgesteld'), tone: 'muted' as const }
  if (match.status === 'cancelled') return { label: translate('Geannuleerd'), tone: 'muted' as const }
  return { label: translate('Ingepland'), tone: 'muted' as const }
}

function getStageLockCopy(stageName?: string | null | undefined) {
  return stageName
    ? translate('Voorspellen is gelockt door de stage release van {stageName}.', getLocale(), { stageName })
    : translate('Voorspellen is tijdelijk gelockt door de stage release.')
}

function formatMinute(value: Pick<FootballMatch, 'minute' | 'extra_minute'> | MatchEvent) {
  if (typeof value.minute !== 'number') return '—'
  if ('extra' in value && typeof value.extra === 'number' && value.extra > 0) {
    return `${value.minute}+${value.extra}'`
  }
  if ('extra_minute' in value && typeof value.extra_minute === 'number' && value.extra_minute > 0) {
    return `${value.minute}+${value.extra_minute}'`
  }
  return `${value.minute}'`
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function getFormDotClass(value: string) {
  const normalized = value.trim().toLowerCase()
  if (normalized.startsWith('w')) return 'bg-emerald-400'
  if (normalized.startsWith('d')) return 'bg-amber-300'
  if (normalized.startsWith('l')) return 'bg-red-400'
  return 'bg-surface-600'
}

function getFormLabel(value: string) {
  const normalized = value.trim().toLowerCase()
  if (normalized.startsWith('w')) return translate('Winst')
  if (normalized.startsWith('d')) return translate('Gelijkspel')
  if (normalized.startsWith('l')) return translate('Verlies')
  return translate('Onbekende vorm')
}

function normalizeEventType(value?: string | null) {
  const normalized = value?.trim().toLowerCase()
  if (normalized === 'goal') return 'goal'
  if (normalized === 'yellow' || normalized === 'yellowcard' || normalized === 'yellow_card') {
    return 'yellow'
  }
  if (normalized === 'red' || normalized === 'redcard' || normalized === 'red_card') {
    return 'red'
  }
  if (normalized === 'sub' || normalized === 'substitution') return 'sub'
  return 'other'
}

function normalizeTeamSide(value?: string | null) {
  const normalized = value?.trim().toLowerCase()
  if (normalized === 'home' || normalized === 'local' || normalized === 'left') return 'home'
  if (normalized === 'away' || normalized === 'visitor' || normalized === 'right') return 'away'
  return 'neutral'
}

function getEventLabel(type: string) {
  if (type === 'goal') return translate('Goal')
  if (type === 'yellow') return translate('Geel')
  if (type === 'red') return translate('Rood')
  if (type === 'sub') return translate('Wissel')
  return translate('Event')
}

function getSideLabel(side: 'home' | 'away' | 'neutral') {
  if (side === 'home') return translate('Thuis')
  if (side === 'away') return translate('Uit')
  return translate('Match')
}

function buildMainEvents(events: MatchEvent[]) {
  const relevantEvents = [...events]
    .sort((left, right) => {
      const minuteDiff = (left.minute ?? 0) - (right.minute ?? 0)
      if (minuteDiff !== 0) return minuteDiff
      return (left.extra ?? 0) - (right.extra ?? 0)
    })
    .filter((event) => {
      const type = normalizeEventType(event.type)
      return type === 'goal' || type === 'yellow' || type === 'red' || isDisallowedGoal(event)
    })

  const items: Array<{ event: MatchEvent; additions: MatchEvent[] }> = []

  for (const event of relevantEvents) {
    if (isDisallowedGoal(event) && items.length > 0) {
      items[items.length - 1]?.additions.push(event)
      continue
    }

    items.push({ event, additions: [] })
  }

  return items
}

function isDisallowedGoal(event: MatchEvent) {
  return (event.addition ?? '').trim().toLowerCase() === 'goal disallowed'
}

function hasLineups(match?: FootballMatch | null) {
  if (!match) return false
  return (
    (match.homeLineup?.length ?? 0) > 0 ||
    (match.homeBench?.length ?? 0) > 0 ||
    (match.awayLineup?.length ?? 0) > 0 ||
    (match.awayBench?.length ?? 0) > 0
  )
}

function groupPlayersByPosition(players: LineupPlayer[]) {
  const groups = new Map<string, { key: string; label: string; players: LineupPlayer[] }>()

  for (const player of players) {
    const key = normalizePosition(player.position)
    const existing = groups.get(key)

    if (existing) {
      existing.players.push(player)
      continue
    }

    groups.set(key, {
      key,
      label: getPositionLabel(key),
      players: [player],
    })
  }

  const order = ['keeper', 'defence', 'midfield', 'attack', 'other']
  return Array.from(groups.values()).sort(
    (left, right) => order.indexOf(left.key) - order.indexOf(right.key),
  )
}

function normalizePosition(value?: string | null) {
  const normalized = value?.trim().toLowerCase() ?? ''
  if (
    normalized.includes('keeper') ||
    normalized === 'gk' ||
    normalized === 'goalkeeper'
  ) {
    return 'keeper'
  }
  if (
    normalized.includes('def') ||
    normalized.includes('back') ||
    normalized.includes('centre-back') ||
    normalized.includes('center-back')
  ) {
    return 'defence'
  }
  if (normalized.includes('mid')) return 'midfield'
  if (
    normalized.includes('att') ||
    normalized.includes('forw') ||
    normalized.includes('wing')
  ) {
    return 'attack'
  }
  return 'other'
}

function getPositionLabel(key: string) {
  if (key === 'keeper') return translate('Keeper')
  if (key === 'defence') return translate('Defence')
  if (key === 'midfield') return translate('Midfield')
  if (key === 'attack') return translate('Attack')
  return translate('Other')
}

function getOutcomeLabel(
  key: string,
  data: MatchPredictionStatsResponse,
) {
  if (key === 'home_win') return `${data.match.home_team.name} wint`
  if (key === 'away_win') return `${data.match.away_team.name} wint`
  if (key === 'draw') return translate('Gelijkspel')
  return key
}
